import { type ChangeEvent, useEffect, useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, Search } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/app/reduxHooks';
import {
  TRANSFORMATION_LOOKUP_DATASOURCE_SELECTION_QUERY_KEY,
  getTransformationLookupDatasourceSelectionQueryKey,
  useRegisteredSources,
} from '@/features/adp/queries/useSourceQueries';
import { closeOverlay, popOverlay } from '@/features/adp/state/overlaySlice';
import { ResolvedIcon } from '@/features/adp/components/shared/ResolvedIcon';
import { Button } from '@/ui/button';
import { Card } from '@/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/ui/dialog';
import { Input } from '@/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/select';
import { Skeleton } from '@/ui/skeleton';
import { useQueryClient } from '@tanstack/react-query';
import type { RegisteredSourceDataSource } from '@/features/adp/services/source/source.types';

const DEFAULT_ITEMS_PER_PAGE = 24;
const ITEMS_PER_PAGE_OPTIONS = [12, 24, 36, 48];

const TransformationDatasourcePickerDialog = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { isOpen, type, stack } = useAppSelector((state) => state.overlay);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [searchValue, setSearchValue] = useState('');
  const [lastTotalCount, setLastTotalCount] = useState(0);

  const isVisible = isOpen && type === 'transformation-datasource-picker';
  const hasTransformationSheetInStack = stack.some((frame) => frame.type === 'transformation-sheet');

  const { data: registeredSourcesResponse, isPending } = useRegisteredSources({
    allowed_instances: '',
    source_name: searchValue,
    enabled: isVisible,
    page,
    limit: itemsPerPage,
  });

  const sources = registeredSourcesResponse?.sources ?? [];
  const totalCount = registeredSourcesResponse?.total_count ?? lastTotalCount;
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / itemsPerPage)),
    [itemsPerPage, totalCount]
  );

  useEffect(() => {
    if (isVisible) {
      setPage(1);
      setSearchValue('');
    }
  }, [isVisible]);

  useEffect(() => {
    if (registeredSourcesResponse && page > totalPages) {
      setPage(totalPages);
    }
  }, [page, registeredSourcesResponse, totalPages]);

  useEffect(() => {
    if (registeredSourcesResponse) {
      setLastTotalCount(registeredSourcesResponse.total_count ?? 0);
    }
  }, [registeredSourcesResponse]);

  const handleClose = () => {
    if (hasTransformationSheetInStack) {
      dispatch(popOverlay());
      return;
    }
    dispatch(closeOverlay());
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value));
    setPage(1);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    setPage(1);
  };

  const handleSourceSelect = (source: RegisteredSourceDataSource) => {
    const sourceId = source.source_data_source_id;
    if (!sourceId) return;

    const selectedLookupDatasource = {
      sourceDataSourceId: sourceId,
      dataSourceName: source.data_source_name,
    };

    queryClient.setQueryData(TRANSFORMATION_LOOKUP_DATASOURCE_SELECTION_QUERY_KEY, selectedLookupDatasource);
    queryClient.setQueryData(
      getTransformationLookupDatasourceSelectionQueryKey(sourceId),
      selectedLookupDatasource
    );
    handleClose();
  };

  return (
    <Dialog
      open={isVisible}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent className="max-w-4xl" onOpenAutoFocus={(event) => event.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Select Data Source</DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchValue}
              onChange={handleSearchChange}
              placeholder="Search data source"
              aria-label="Search data source"
              className="pl-9"
            />
          </div>

          <div className="grid max-h-[420px] grid-cols-1 gap-3 overflow-y-auto pr-1 sm:grid-cols-2 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border">
            {isPending &&
              Array.from({ length: itemsPerPage }, (_, serial) => serial + 1).map((skeletonId) => (
                <Card key={`transformation-datasource-skeleton-${skeletonId}`} className="border p-3 shadow-none">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-9 w-9 rounded-md" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                </Card>
              ))}

            {!isPending &&
              sources.map((source) => (
                <Card
                  key={source.source_data_source_id ?? source.roo_id ?? source.data_source_name}
                  className="cursor-pointer border p-3 shadow-none transition-colors hover:border-primary/40"
                  onClick={() => handleSourceSelect(source)}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-md border">
                      <ResolvedIcon
                        imageLink={source.image_link}
                        alt={source.data_source_name}
                        className="h-7 w-7 object-contain"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">{source.data_source_name}</p>
                      <p className="truncate text-xs text-muted-foreground">{source.data_source_instance}</p>
                    </div>
                  </div>
                </Card>
              ))}

            {!isPending && sources.length === 0 && (
              <div className="col-span-full rounded-md border border-dashed p-5 text-center text-sm text-muted-foreground">
                No data sources found.
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Items per page</span>
              <Select value={String(itemsPerPage)} onValueChange={handleItemsPerPageChange}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                    <SelectItem key={option} value={String(option)}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={page <= 1}
                onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
                aria-label="Previous page"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={page >= totalPages}
                onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
                aria-label="Next page"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default TransformationDatasourcePickerDialog;
